#!/usr/bin/env node
import type { TransactionReceipt } from "@saberhq/solana-contrib";
import { u64 } from "@saberhq/token-utils";
import { PublicKey } from "@solana/web3.js";
import * as assert from "assert";
import { Command } from "commander";
import { exit } from "process";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import { QUARRY_CODERS } from "../src";
import { findTransfererAddress } from "../src/wrappers/transferWrapper";
import { makeSDK, printObjectJSON, printObjectTable } from "./utils";

export const program = new Command().name("crema-transfer-wrapper");
program.version(pack.version);

// New a transfer wrapper
program
  .command("new")
  .description("Create a transfer wrapper")
  .requiredOption("-m, --mint <mint>", "The token mint address")
  .action((arg) => {
    const m = new PublicKey(arg.mint);
    newTransferWrapper(m)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

// New a transferer
program
  .command("new-transferer")
  .description("Create a new transferer for transfer wrapper")
  .requiredOption("-w, --wrapper <wrapper>", "The transfer wrapper address")
  .argument("<authority>", "The transferer authority address")
  .action((authority, arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(authority);
    newTransferer(w, a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

// Fetches info on a transfer Wrapper.
program
  .command("info")
  .description("Get the transfer wrapper information")
  .argument("<wrapper>", "The transfer wrapper address.")
  .action((wrapper: string) => {
    const w = new PublicKey(wrapper);
    fetchTransferWrapper(w).catch((err) => {
      console.log(err);
    });
  });

// Fetches info on a transferer
program
  .command("transferer-info")
  .description("Get the transferer information")
  .requiredOption("-w, --wrapper   <wrapper>", "The transfe wrapper address")
  .requiredOption(
    "-a, --authority <authority>",
    "The authority of the transferer"
  )
  .action((arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(arg.authority);
    fetchTransferer(w, a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

// Update a transferer's allowance
program
  .command("update-transferer")
  .description("Update the transferer allowance")
  .requiredOption("-w, --wrapper <wrapper>", "The transfer wrapper address")
  .requiredOption(
    "-a, --authority <authority>",
    "The authority of the transferer"
  )
  .argument("<allowance>", "The transferer's new allowance. consider decimals")
  .action((allowance, arg) => {
    const w = new PublicKey(arg.wrapper);
    const a = new PublicKey(arg.authority);
    const al = new u64(allowance);
    transfererUpdate(w, a, al)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

// Update a transferer wrapper's cap
program
  .command("update-cap")
  .description("Update the transfer wrapper cap")
  .requiredOption("-w, --wrapper <wrapper>", "The transfer wrapper address")
  .argument("<cap>", "The transfer wrapper's new cap. consider decimals")
  .action((cap, arg) => {
    const w = new PublicKey(arg.wrapper);
    const al = new u64(cap);
    updateCap(w, al)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

// withdraw
program
  .command("withdraw")
  .description("withdraw token from program")
  .requiredOption("-w, --wrapper <wrapper>", "The transfer wrapper address")
  .requiredOption(
    "-d, --destination <destination>",
    "The destination token account"
  )
  .action((arg) => {
    const w = new PublicKey(arg.wrapper);
    const dest = new PublicKey(arg.destination);
    withdraw(w, dest)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

async function newTransferWrapper(
  mint: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = await sdk.transferWrapper.newWrapper({
    tokenMint: mint,
  });

  const receipt = await tx.confirm();
  const event = QUARRY_CODERS.TransferWrapper.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  )[0];
  invariant(
    event?.name === "NewTransferWrapperEvent",
    "new transfer wrapper event not found"
  );
  assert.ok(event, "new transfer wrapper event not found");
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    event: {
      ...event.data,
    },
  });

  return receipt;
}

async function newTransferer(
  wrapper: PublicKey,
  authority: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();

  const tx = await sdk.transferWrapper.newTransferer(wrapper, authority);

  const receipt = await tx.confirm();
  const event = QUARRY_CODERS.TransferWrapper.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  )[0];
  invariant(
    event?.name === "NewTransferEvent",
    "new transferer event not found"
  );
  assert.ok(event, "new transferer event not found");
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    event: {
      ...event.data,
    },
  });

  return receipt;
}

async function transfererUpdate(
  wrapper: PublicKey,
  authority: PublicKey,
  allowance: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = await sdk.transferWrapper.transfererUpdate(
    wrapper,
    authority,
    allowance
  );
  const receipt = await tx.confirm();
  const event = QUARRY_CODERS.TransferWrapper.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  )[0];
  invariant(
    event?.name === "TransfererAllowanceUpdateEvent",
    "transferer allowance update event not found"
  );
  assert.ok(event, "transferer allowance update event not found");
  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    event: {
      ...event.data,
    },
  });

  return receipt;
}

async function updateCap(
  wrapper: PublicKey,
  cap: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = await sdk.transferWrapper.updateCap(wrapper, cap);
  const receipt = await tx.confirm();

  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    cap: cap.toNumber(),
    wrapper: wrapper.toBase58(),
  });

  return receipt;
}

async function withdraw(
  wrapper: PublicKey,
  dest: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const tx = await sdk.transferWrapper.withdraw(wrapper, dest);
  const receipt = await tx.confirm();

  printObjectJSON({
    signature: receipt.signature.toString(),
    computeUnits: receipt.computeUnits,
    dest: dest.toBase58(),
    wrapper: wrapper.toBase58(),
  });

  return receipt;
}

async function fetchTransferWrapper(wrapper: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.transferWrapper.fetchTransferWrapper(wrapper);
  if (info === null) {
    console.log("The transfer wrapper: ", wrapper.toBase58(), "not found.");
    return;
  }
  printObjectTable({
    wrapper: wrapper,
    ...info,
  });
}

async function fetchTransferer(wrapper: PublicKey, authority: PublicKey) {
  const sdk = makeSDK();
  const info = await sdk.transferWrapper.fetchTransferer(wrapper, authority);
  if (info === null) {
    console.log("The transferer not found");
    return;
  }
  const [transfererAddress] = await findTransfererAddress(
    wrapper,
    authority,
    sdk.transferWrapper.program.programId
  );
  printObjectTable({
    transferer: transfererAddress,
    ...info,
  });
}
